<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { confirmPersonName } from '../../lib/convexApi';
  import { markNameConfirmed } from '../session';
  import Notice from '../ui/Notice.svelte';

  // Prefilled from the Clerk display name; always editable. The consent
  // checkbox starts UNCHECKED (explicit opt-in). One field, not a wizard —
  // the only way out without answering is to sign out.
  export let initialName = '';

  const dispatch = createEventDispatcher<{ signout: void }>();

  let name = initialName;
  let consent = false;
  let pending = false;
  let error: string | null = null;

  // Prefill only until the user starts typing.
  let prefillKey = '';
  $: if (prefillKey !== initialName && !pending && name === '') {
    name = initialName;
    prefillKey = initialName;
  }

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      error = 'Please enter a name.';
      return;
    }
    pending = true;
    error = null;
    try {
      const person = await confirmPersonName(trimmed, consent);
      const confirmedAt =
        person && typeof person.nameConfirmedAt === 'number' ? person.nameConfirmedAt : Date.now();
      const confirmedName = person && typeof person.name === 'string' ? person.name : trimmed;
      const consentStatus = consent ? 'granted' : 'declined';
      markNameConfirmed(confirmedAt, consentStatus, confirmedName);
    } catch (err) {
      error = err instanceof Error ? err.message : 'Could not save your name. Please try again.';
      pending = false;
    }
  }
</script>

<div class="namegate" role="dialog" aria-modal="true" aria-labelledby="namegate-title">
  <section class="namegate__panel surface surface--raised">
    <header class="namegate__header">
      <h1 id="namegate-title" class="namegate__title">What do we call you?</h1>
      <p class="namegate__lede">
        Your name shows on guest lists and invites. Set it once — you can change it later.
      </p>
    </header>

    {#if error}
      <Notice variant="error">{error}</Notice>
    {/if}

    <form class="namegate__form" on:submit|preventDefault={submit}>
      <label class="namegate__field">
        <span class="namegate__label">Your name</span>
        <input
          class="namegate__input"
          type="text"
          bind:value={name}
          maxlength="80"
          autocomplete="name"
          placeholder="First and last name"
          disabled={pending}
        />
      </label>

      <label class="namegate__consent">
        <input type="checkbox" bind:checked={consent} disabled={pending} />
        <span>Send me updates about events</span>
      </label>

      <button type="submit" class="accent namegate__submit" disabled={pending}>
        {pending ? 'Saving…' : 'Continue'}
      </button>
    </form>

    <button
      type="button"
      class="ghost namegate__signout"
      on:click={() => dispatch('signout')}
      disabled={pending}
    >
      Sign out
    </button>
  </section>
</div>

<style>
  .namegate {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    min-height: 60vh;
    padding: var(--space-6) var(--space-4);
    box-sizing: border-box;
  }

  .namegate__panel {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    width: 100%;
    max-width: 26rem;
    padding: var(--space-6);
    border-radius: var(--radius-xl);
  }

  .namegate__header {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .namegate__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--font-size-xl);
    font-weight: 800;
  }

  .namegate__lede {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
  }

  .namegate__form {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .namegate__field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .namegate__label {
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
  }

  .namegate__input {
    width: 100%;
    box-sizing: border-box;
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: var(--font-size-md);
  }

  .namegate__consent {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    cursor: pointer;
  }

  .namegate__consent input {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    accent-color: var(--color-accent);
    cursor: pointer;
  }

  .namegate__submit {
    width: 100%;
  }

  .namegate__signout {
    align-self: center;
    font-size: var(--font-size-sm);
  }
</style>
